import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cx } from "../lib/cx";

/**
 * 모달 — 원본 `app.css` 의 `.modal-backdrop` / `.modal` 과 `app.js` 의 `openModal` 동작.
 *
 * 원본이 `document.body` 에 직접 붙였으므로 React 에서도 **포털**로 body 에 렌더합니다. 셸 안에
 * 두면 sticky 헤더의 `z-index`·`backdrop-filter` 가 만드는 쌓임 문맥에 갇혀 배경 딤이 헤더를
 * 덮지 못합니다.
 *
 * 동작 세 가지를 그대로 옮겼습니다.
 *  - 배경(백드롭 자신)을 클릭하면 닫습니다. 내용 클릭은 버블링으로 올라와도 무시해야 하므로
 *    `ev.target === ev.currentTarget` 으로 구분합니다.
 *  - Escape 로 닫습니다. 모달 위에 확인 창이 겹쳐 뜨는 경우가 있으므로 **맨 위 모달만** 반응합니다
 *    (아래 `stack`) — 아니면 Escape 한 번에 확인 창과 그 아래 모달이 함께 닫힙니다.
 *  - 열릴 때 **한 번** 첫 입력 요소로 포커스를 옮겨 키보드만으로 바로 작업할 수 있게 합니다.
 *    onClose 는 ref 로 읽어 effect 를 마운트에 묶습니다 — 호출부의 onClose 가 진행 상태 등에 따라
 *    바뀔 때마다 포커스가 첫 입력으로 튀지 않게 합니다.
 */
/** 열린 모달의 순서(아래→위). 모듈 전역인 이유: 모달끼리 서로를 몰라도 맨 위를 판정해야 합니다. */
const stack: symbol[] = [];

export function Modal({
  title,
  sub,
  onClose,
  children,
  footer,
  status,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  children?: ReactNode;
  footer: ReactNode;
  /**
   * 제출 결과·입력 문제 안내 — 본문이 아니라 **푸터 바로 위**에 둡니다. 긴 폼은 스크롤되므로
   * 맨 위에 두면 버튼을 누른 자리에서 보이지 않아 "눌러도 반응이 없는" 것처럼 보입니다.
   */
  status?: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(Symbol("modal"));
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const id = idRef.current;
    stack.push(id);
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && stack[stack.length - 1] === id) onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    dialogRef.current
      ?.querySelector<HTMLElement>("input:not([disabled]), select, textarea, button:not([disabled])")
      ?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const at = stack.lastIndexOf(id);
      if (at >= 0) stack.splice(at, 1);
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(25,25,28,0.48)] p-[24px]"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget && stack[stack.length - 1] === idRef.current) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[86vh] w-full max-w-[480px] overflow-y-auto rounded-[20px] bg-white p-[24px] shadow-card"
      >
        <h2 className="m-0 mb-[4px] text-[18px] font-bold tracking-[-0.02em]">{title}</h2>
        {sub ? <p className="m-0 mb-[20px] text-[13px] text-grey-500">{sub}</p> : null}
        {children}
        {/*
          상태 문구 + 버튼은 스크롤해도 아래에 붙어 있습니다(sticky). 긴 폼에서 버튼을 찾으러 내려가거나,
          눌렀는데 결과가 화면 밖에 뜨는 일을 없앱니다. 음수 여백으로 대화상자 패딩까지 덮어 내용이
          비치지 않게 합니다.
        */}
        <div className="sticky bottom-[-24px] -mx-[24px] -mb-[24px] mt-[4px] bg-white px-[24px] pb-[24px] pt-[16px]">
          {status ? <div className="mb-[16px] [&>*:last-child]:mb-0">{status}</div> : null}
          <div className="flex justify-end gap-[8px]">{footer}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** 모달 본문의 폼 묶음 — 원본 `.modal-form`. */
export function ModalForm({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("flex flex-col gap-[14px]", className)}>{children}</div>;
}
